import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import { adoptLegacyStorage, removeProductStorage, setCurrentProductId } from './productStorage';
import { isOnboarding } from './productUtils';

// The user's products and the one every screen is working on
const ProductContext = createContext(null);

const STORAGE_KEY = 'currentProductId';

const NO_ICPS = [];

// Links from emails name the product (?product=<id>), so they open on it;
// otherwise the product last used in this browser
const readStoredId = () => {
    const linked = new URLSearchParams(window.location.search).get('product');
    if (linked && /^[0-9a-f-]{36}$/i.test(linked)) {
        try {
            localStorage.setItem(STORAGE_KEY, linked);
        } catch (e) {
            // Storage unavailable; the link still opens on this product
        }
        return linked;
    }
    try {
        return localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
        return '';
    }
};

export const ProductProvider = ({ user, children }) => {
    const [products, setProducts] = useState([]);
    const [selectedId, setSelectedId] = useState(readStoredId);
    // ICPs are kept with the product they belong to, so a switch never shows the last product's
    const [icpState, setIcpState] = useState({ productId: '', items: NO_ICPS });
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const [loadError, setLoadError] = useState('');

    // Fall back to a set-up product, then any product, when the stored one is gone
    const currentProduct = products.find(product => product.id === selectedId)
        || products.find(product => !isOnboarding(product))
        || products[0]
        || null;
    // Set during render so the first requests of child screens are already scoped
    setCurrentProductId(currentProduct?.id);
    const currentId = currentProduct?.id || '';
    const currentIdRef = useRef(currentId);
    currentIdRef.current = currentId;
    const userId = user?.id;

    const loadProducts = useCallback(async () => {
        if (!userId) return;
        setIsLoadingProducts(true);
        setLoadError('');
        try {
            setProducts(await api.listProducts());
        } catch (e) {
            setLoadError("Couldn't load your products. Check that the server is running, then try again.");
        } finally {
            setIsLoadingProducts(false);
        }
    }, [userId]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const loadIcps = useCallback(async () => {
        const productId = currentIdRef.current;
        if (!productId) return;
        let items = NO_ICPS;
        try {
            items = await api.listIcps(productId);
        } catch (e) {
            // Screens show their empty state
        }
        setIcpState({ productId, items });
    }, []);

    // While the products are still loading, the ICPs of the remembered product load alongside them
    const icpProductId = currentId || (isLoadingProducts && userId ? selectedId : '');
    useEffect(() => {
        if (!icpProductId) return undefined;
        let cancelled = false;
        api.listIcps(icpProductId)
            .catch(() => NO_ICPS)
            .then(items => !cancelled && setIcpState({ productId: icpProductId, items }));
        return () => {
            cancelled = true;
        };
    }, [icpProductId]);

    const icps = icpState.productId === currentId ? icpState.items : NO_ICPS;
    // Screens mount once the product and its ICPs are both in, so nothing pops in after them
    const isLoading = isLoadingProducts || (Boolean(currentId) && icpState.productId !== currentId);

    // `next` is a list or an updater; `productId` is for a product that is about to become current
    const setIcps = useCallback((next, productId = currentIdRef.current) => {
        setIcpState(prev => {
            const previous = prev.productId === productId ? prev.items : NO_ICPS;
            return { productId, items: typeof next === 'function' ? next(previous) : next };
        });
    }, []);

    const selectProduct = useCallback((id) => {
        setSelectedId(id);
        try {
            localStorage.setItem(STORAGE_KEY, id);
        } catch (e) {
            // Storage unavailable; selection lasts for this session
        }
    }, []);

    const replaceProduct = useCallback((product) => {
        setProducts(prev => (prev.some(p => p.id === product.id)
            ? prev.map(p => (p.id === product.id ? product : p))
            : [...prev, product]));
        return product;
    }, []);

    const createProduct = useCallback(async (fields) => {
        const product = await api.createProduct(fields);
        if (product.adoptedLegacy) adoptLegacyStorage(product.id);
        return replaceProduct(product);
    }, [replaceProduct]);

    const updateProduct = useCallback(async (id, changes) => replaceProduct(await api.updateProduct(id, changes)), [replaceProduct]);

    const deleteProduct = useCallback(async (id) => {
        await api.deleteProduct(id);
        removeProductStorage(id);
        setProducts(prev => prev.filter(p => p.id !== id));
    }, []);

    const value = useMemo(() => ({
        products,
        currentProduct,
        icps,
        isLoading,
        loadError,
        selectProduct,
        loadProducts,
        loadIcps,
        setIcps,
        createProduct,
        updateProduct,
        deleteProduct,
        replaceProduct,
    }), [products, currentProduct, icps, isLoading, loadError, selectProduct, loadProducts, loadIcps, setIcps,
        createProduct, updateProduct, deleteProduct, replaceProduct]);

    return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

export const useProducts = () => useContext(ProductContext);
